import React, { useState } from 'react';
import { Flag } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import { apiClient } from '../api';
import { REPORT_REASONS } from '../lib/starters';

const ReportDialog = ({ targetUserId, targetName, trigger }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason) {
      toast.error('Pick a reason');
      return;
    }
    setBusy(true);
    try {
      await apiClient.post('/reports', {
        target_user_id: targetUserId,
        reason,
        details: details.trim() || null,
      });
      toast.success('Report submitted', {
        description: 'Thanks — our team will review it. You can block them too for extra safety.',
      });
      setOpen(false);
      setReason('');
      setDetails('');
    } catch (err) {
      toast.error('Could not submit report', { description: err.response?.data?.detail || '' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" className="text-purple-300 hover:text-red-400" data-testid="report-trigger">
            <Flag className="w-4 h-4 mr-1.5" />Report
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-fnl-purple border-purple-900 text-white max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Report {targetName || 'this user'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-purple-200 text-xs uppercase tracking-wider font-semibold mb-2 block">Why?</Label>
            <RadioGroup value={reason} onValueChange={setReason}>
              {REPORT_REASONS.map((r) => (
                <div key={r.value} className="flex items-center gap-2 py-1">
                  <RadioGroupItem value={r.value} id={`reason-${r.value}`} className="border-fnl-pink text-fnl-pink" />
                  <Label htmlFor={`reason-${r.value}`} className="text-purple-100 cursor-pointer" data-testid={`reason-${r.value}`}>
                    {r.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label className="text-purple-200 text-xs uppercase tracking-wider font-semibold mb-2 block">
              Anything else? <span className="text-purple-400 normal-case font-normal">(optional)</span>
            </Label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Give us a quick description..."
              className="bg-white border-0 text-gray-900"
              data-testid="report-details"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="bg-fnl-purple-light/40 border-purple-700 text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason} className="bg-fnl-pink hover:bg-fnl-pink text-white" data-testid="report-submit">
            {busy ? 'Sending...' : 'Submit report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDialog;
